import objectHandle from "../../../utilsTs/baseLib/objectHandle";
import { defaultRtn, MethodType } from "../../../utilsTs/declareTypes";


/** fetchApi选项 */
export declare interface FetchApiOptions {
    /** query 查询内容 */
    query?: any;
    /** headers头 */
    headers?: any;
    /** body */
    body?: any;
    /** token值 */
    token?: string;
};

/**
 * 请求数据
 * @param method 方法
 * @param url 地址
 * @param options 选项
 * @returns 
 */
const request = async (method: MethodType,
    url: string,
    options?: FetchApiOptions) => {
    const rtn = defaultRtn();
    try {
        let { query, headers, body,
            token = localStorage.getItem('token') ? localStorage.getItem('token') : sessionStorage.getItem('token') } = options || {};

        if (method === 'GET') {
            body = undefined;
        } else {
            body = body && JSON.stringify(body);
        }

        if (query) {
            let paramsArray: string[] = [];
            Object.keys(query).forEach(key =>
                paramsArray.push(
                    `${encodeURIComponent(key)}=${encodeURIComponent(
                        objectHandle.isObject(query[key])
                            ? JSON.stringify(query[key])
                            : query[key]
                    )}`
                ))
            if (url.search(/\?/) === -1) {
                url += `?${paramsArray.join('&')}`;
            } else {
                url += `&${paramsArray.join('&')}`;
            }
        }

        const response = await fetch(url, {
            method,
            // 【修复】credentials 属于 fetch 的请求选项，不是 header；
            // 原来写在 headers 里会导致 cookie 实际不会携带
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                // 【修复】token 为空时不发送 authorization header，
                // 避免某些实现将其序列化成 "undefined" 字符串
                ...(token ? { 'authorization': `Bearer ${token}` } : {}),
                ...headers
            },
            body
        });

        // 【修复】Response body 只能被读取一次（body stream already read），
        // 因此统一只读一次 text，再按需手动 JSON.parse，
        // 避免 response.json() 失败后 catch 里再 response.text() 触发二次读取异常。
        const text = await response.text();

        // 先尝试将响应体解析为 JSON
        let json: any;
        let isJson = false;
        try {
            json = text ? JSON.parse(text) : null;
            isJson = true;
        } catch {
            isJson = false;
        }

        // 非 JSON 响应（如 HTML 错误页、空 body、乱码等），直接使用文本
        if (isJson === false) {
            rtn.success = response.ok;
            rtn.data = text;
            rtn.message = response.ok
                ? ''
                : `请求失败，状态码：${response.status}${text ? `，响应：${text.slice(0, 200)}` : ''}`;
            return rtn;
        }

        // 如果后端返回的是标准的 RtnType 结构（包含 success 字段）
        if (json && typeof json.success === 'boolean') {
            // 直接使用后端返回的完整结构，确保 message、data 等字段存在
            return { ...defaultRtn(), ...json };
        }

        // 普通 JSON 数据（非 RtnType 结构）
        if (response.ok) {
            rtn.success = true;
            rtn.data = json;
        } else {
            rtn.success = false;
            rtn.message = json?.message || `请求失败，状态码：${response.status}`;
            rtn.data = json;
        }
        return rtn;

    } catch (error: any) {
        rtn.success = false;
        rtn.message = '网络错误！';
        if (error) {
            rtn.message = `网络错误！${error.message || error}`;
        }
        return rtn;
    }
};

/** fetchApi 数据接口 */
const fetchApi = {
    /**
     * fetchApi GET 
     * @param url 请求地址
     * @param options 选项
     * @returns 
     */
    GET: (url: string, options?: FetchApiOptions) => request('GET', url, options),

    /**
     * fetchApi POST
     * @param url 请求地址
       @param options 选项
     * @returns 返回获取的数据
     */
    POST: (url: string, options?: FetchApiOptions) => request('POST', url, options),

    /**
     * fetchApi PUT
     * @param url 请求地址
     * @param options 选项
     * @returns 返回获取的数据
     */
    PUT: (url: string, options?: FetchApiOptions) => request('PUT', url, options),

    /**
     * fetchApi DELETE
     * @param url 请求地址
     * @param options 选项
     * @returns 返回获取的数据
     */
    DELETE: (url: string, options?: FetchApiOptions) => request('DELETE', url, options),
};

/** fetchApi 数据接口 */
export default fetchApi;